import React, { ComponentProps } from 'react';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { Direction, TabPanel, Tabs } from '../../../components/molecules/Tabs';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { MostPopularPagesTable, PageItem } from '../../../_lib/components/MostPopularPagesTable/MostPopularPagesTable';
import { MostPopularPagesExport } from '../partials/MostPopularPagesExport';

export type CsvFileLink = {
  fileName: string;
  url: string;
  fileExtension: string;
  mimeType: string;
  fileSizeBytes: number;
  isApproximateSize: boolean;
};

export type CsvExport = {
  last30DayReport: CsvFileLink;
  monthlyReports: CsvFileLink[];
};

export type MostPopularPagesStatisticsViewProps = {
  mostVisitedPages: PageItem[];
  mostWatchedPages: PageItem[];
  mostFavoritedPages: PageItem[];
  export?: CsvExport;
};

/**
 * Displays the three most popular pages tables (visited, watched, favorited) and a CSV export tab
 * using a vertical tab bar.
 */
function MostPopularPagesStatistics({
  mostVisitedPages,
  mostWatchedPages,
  mostFavoritedPages,
  export: exportData,
}: MostPopularPagesStatisticsViewProps) {
  const { t } = useSonTranslation('MostPopularPages');

  return (
    <>
      <Tabs initialActiveTab="most-popular-pages-tab-visited" direction={Direction.VERTICAL} label={t('tabs.label')}>
        <TabPanel buttonLabel={t('table.mostVisited')} id="most-popular-pages-tab-visited">
          <MostPopularPagesTable
            caption={t('caption.visited')}
            items={mostVisitedPages}
            countLabelPostFix={t('countLabelPostFix.visited')}
            emptyNotice={t('notice.noVisits')}
          />
        </TabPanel>
        <TabPanel buttonLabel={t('table.mostWatched')} id="most-popular-pages-tab-watched">
          <MostPopularPagesTable
            caption={t('caption.watched')}
            items={mostWatchedPages}
            countLabelPostFix={t('countLabelPostFix.watched')}
            emptyNotice={t('notice.noWatches')}
          />
        </TabPanel>
        <TabPanel buttonLabel={t('table.mostFavorited')} id="most-popular-pages-tab-favorited">
          <MostPopularPagesTable
            caption={t('caption.favorites')}
            items={mostFavoritedPages}
            countLabelPostFix={t('countLabelPostFix.favorites')}
            emptyNotice={t('notice.noFavorites')}
          />
        </TabPanel>
        <TabPanel buttonLabel={t('export.tabTitle')} id="most-popular-pages-tab-export">
          {exportData && <MostPopularPagesExport {...exportData} />}
        </TabPanel>
      </Tabs>
    </>
  );
}

export const MostPopularPagesStatisticsView = asFetchableView(MostPopularPagesStatistics);
export type MostPopularPagesStatisticsView = ComponentProps<typeof MostPopularPagesStatisticsView>;
